import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Save, Search, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function CategoryManager() {
  const [items, setItems] = useState([]);
  const [articleCounts, setArticleCounts] = useState({});
  const [form, setForm] = useState({ name: "", slug: "", description: "", icon: "", color: "" });
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const load = async () => {
    const catRes = await api.get("/categories").catch(() => ({ data: [] }));
    setItems(catRes.data || []);
    // Fetch article counts per category
    const counts = {};
    for (const cat of (catRes.data || [])) {
      try {
        const r = await api.get(`/articles?category_id=${cat.id}&limit=0`);
        counts[cat.id] = r.data.total || 0;
      } catch { counts[cat.id] = 0; }
    }
    setArticleCounts(counts);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.slug) { toast.error("Name and slug required"); return; }
    try {
      if (editId) await api.put(`/categories/${editId}`, form);
      else await api.post("/categories", form);
      toast.success(editId ? "Updated" : "Created");
      setOpen(false);
      setForm({ name: "", slug: "", description: "", icon: "", color: "" });
      setEditId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleEdit = (item) => {
    setForm({ name: item.name, slug: item.slug, description: item.description || "", icon: item.icon || "", color: item.color || "" });
    setEditId(item.id);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category? Articles in this category won't be deleted.")) return;
    try { await api.delete(`/categories/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  const openNew = () => {
    setForm({ name: "", slug: "", description: "", icon: "", color: "" });
    setEditId(null);
    setOpen(true);
  };

  const filtered = items.filter(item =>
    !searchTerm || item.name.toLowerCase().includes(searchTerm.toLowerCase()) || item.slug.includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="category-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-serif font-bold text-foreground">Categories</h1>
          <p className="text-sm font-sans text-muted-foreground mt-1">{items.length} categories</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E88E5] hover:bg-[#1565C0] font-sans" onClick={openNew} data-testid="new-category-btn">
              <Plus className="h-4 w-4 mr-2" /> New Category
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif">{editId ? "Edit" : "New"} Category</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-4">
              <div><Label className="font-sans text-sm">Name *</Label><Input value={form.name} onChange={e => { set("name", e.target.value); if (!editId) set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")); }} className="mt-1" data-testid="category-name" /></div>
              <div><Label className="font-sans text-sm">Slug *</Label><Input value={form.slug} onChange={e => set("slug", e.target.value)} className="mt-1" data-testid="category-slug" /></div>
              <div><Label className="font-sans text-sm">Description</Label><Input value={form.description} onChange={e => set("description", e.target.value)} className="mt-1" data-testid="category-description" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="font-sans text-sm">Icon</Label><Input value={form.icon} onChange={e => set("icon", e.target.value)} placeholder="Newspaper" className="mt-1" /></div>
                <div>
                  <Label className="font-sans text-sm">Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={form.color} onChange={e => set("color", e.target.value)} placeholder="#1E88E5" className="flex-1" />
                    {form.color && <div className="w-10 h-10 border border-slate-200 shrink-0" style={{ backgroundColor: form.color }} />}
                  </div>
                </div>
              </div>
              <Button onClick={handleSave} className="w-full bg-[#1E88E5] hover:bg-[#1565C0] font-sans" data-testid="save-category"><Save className="h-4 w-4 mr-2" />Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search categories..." className="pl-9 h-10" data-testid="category-search" />
      </div>

      {/* Category Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(item => (
          <div key={item.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 flex items-start gap-3 group hover:border-slate-400 dark:hover:border-slate-500 transition-colors" data-testid={`admin-cat-${item.id}`}>
            {/* Color swatch */}
            <div className="w-10 h-10 shrink-0 flex items-center justify-center" style={{ backgroundColor: item.color || '#E5E7EB' }}>
              <span className="text-white font-sans text-xs font-bold">{(item.name || "?")[0]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-sans font-semibold text-sm text-foreground truncate">{item.name}</h3>
                <Badge variant="outline" className="text-[10px] font-sans shrink-0">
                  <FileText className="h-3 w-3 mr-1" />
                  {articleCounts[item.id] ?? "..."}
                </Badge>
              </div>
              <p className="text-[11px] font-sans text-muted-foreground truncate mt-0.5">/{item.slug}</p>
              {item.description && <p className="text-xs font-sans text-muted-foreground mt-1 line-clamp-1">{item.description}</p>}
            </div>
            <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(item)} data-testid={`edit-cat-${item.id}`}><Pencil className="h-3 w-3" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleDelete(item.id)} data-testid={`delete-cat-${item.id}`}><Trash2 className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
